import React from 'react';
import { Box, TextField, Typography, Select, MenuItem, Paper, useTheme, Switch, FormControlLabel, RadioGroup, Radio, Tooltip, Button } from '@mui/material';
import { PatchOptionsState } from './PatchContent';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const PLATFORM_OPTIONS = [
  { label: 'IBM AIX on POWER Systems (64-Bit)', value: 'AIX.x64' },
  { label: 'LINUX ARM (aarch64)', value: 'ARM.x64' },
  { label: 'Oracle Solaris on SPARC (64-Bit)', value: 'SPARC.x64' },
  { label: 'Oracle Solaris on x86-64 (64-Bit)', value: 'SOLARIS.x64' },
  { label: 'Microsoft Windows x64 (64-bit)', value: 'WINDOWS.X64' },
  { label: 'Linux x86-64', value: 'LINUX.X64' },
];

export interface BasicInformationProps {
  sid: string;
  setSid: (v: string) => void;
  sourceHome: string;
  setSourceHome: (v: string) => void;
  targetHome: string;
  setTargetHome: (v: string) => void;
  platform: string;
  setPlatform: (v: string) => void;
  winCredential: string;
  setWinCredential: (v: string) => void;
  deleteWinCredentialFile: boolean;
  setDeleteWinCredentialFile: (v: boolean) => void;
  placeholders: {
    sid: string;
    source_home: string;
    target_home: string;
  };
  patchKey: string;
  patchLabel?: string;
  logDir: string;
  setLogDir: (v: string) => void;
  executionMode?: string;
  targetVersionEnabled?: boolean;
  setTargetVersionEnabled?: (v: boolean) => void;
  targetVersion?: string;
  setTargetVersion?: (v: string) => void;
  targetPlatform?: string;
  setTargetPlatform?: (v: string) => void;
  downloadKeystorePath?: string;
  setDownloadKeystorePath?: (v: string) => void;
  sourceHomeTouched?: boolean;
  setSourceHomeTouched?: (v: boolean) => void;
  targetHomeTouched?: boolean;
  setTargetHomeTouched?: (v: boolean) => void;
  downloadMode?: boolean;
  keystorePath: string;
  setKeystorePath: (v: string) => void;
  sourceHomeEnabled?: boolean;
  setSourceHomeEnabled?: (v: boolean) => void;
  patchOptions: PatchOptionsState;
  oracleBaseEnabled: boolean;
  setOracleBaseEnabled: (v: boolean) => void;
  oracleBase: string;
  setOracleBase: (v: string) => void;
  editionEnabled: boolean;
  setEditionEnabled: (v: boolean) => void;
  edition: string;
  setEdition: (v: string) => void;
  oraInventoryEnabled: boolean;
  setOraInventoryEnabled: (v: boolean) => void;
  oraInventory: string;
  setOraInventory: (v: string) => void;
  inventoryGroupEnabled: boolean;
  setInventoryGroupEnabled: (v: boolean) => void;
  inventoryGroup: string;
  setInventoryGroup: (v: string) => void;
  ignorePrereqFailure?: boolean;
  setIgnorePrereqFailure?: (v: boolean) => void;
  renderValidationMessages?: (msgs: { type: 'success' | 'warning'; content: React.ReactNode }[]) => void;
}

const BasicInformation: React.FC<BasicInformationProps> = ({
  sid, setSid,
  sourceHome, setSourceHome,
  targetHome, setTargetHome,
  platform, setPlatform,
  winCredential, setWinCredential,
  deleteWinCredentialFile, setDeleteWinCredentialFile,
  placeholders,
  patchKey,
  patchLabel,
  logDir,
  setLogDir,
  downloadMode = false,
  keystorePath,
  setKeystorePath,
  targetVersionEnabled,
  setTargetVersionEnabled,
  targetVersion,
  setTargetVersion,
  sourceHomeEnabled,
  setSourceHomeEnabled,
  patchOptions,
  executionMode,
  oracleBaseEnabled,
  setOracleBaseEnabled,
  oracleBase,
  setOracleBase,
  editionEnabled,
  setEditionEnabled,
  edition,
  setEdition,
  oraInventoryEnabled,
  setOraInventoryEnabled,
  oraInventory,
  setOraInventory,
  inventoryGroupEnabled,
  setInventoryGroupEnabled,
  inventoryGroup,
  setInventoryGroup,
  ignorePrereqFailure,
  setIgnorePrereqFailure,
  renderValidationMessages,
}) => {
  const theme = useTheme();

  // Floating placeholder logic for each field
  const [sidTouched, setSidTouched] = React.useState(false);
  const [sourceHomeTouched, setSourceHomeTouched] = React.useState(false);
  const [targetHomeTouched, setTargetHomeTouched] = React.useState(false);
  const [logDirTouched, setLogDirTouched] = React.useState(false);
  const [pathType, setPathType] = React.useState<'dynamic' | 'dba_defined'>('dba_defined');

  // Dynamic placeholders for Windows
  const isWindows = platform === 'WINDOWS.X64';
  const sourceVersionSuffix = (targetVersion === '23' || targetVersion === '26') ? '23.9'
    : targetVersion === '21' ? '21.3'
    : '19.15';
  const sourceHomePlaceholder = isWindows
    ? `C:\\Oracle\\Product\\${sourceVersionSuffix}\\dbhome_1`
    : `/u01/app/oracle/product/${sourceVersionSuffix}/dbhome_1`;
  
  // Calculate target home placeholder based on target version or RU version
  const getTargetHomePlaceholder = React.useMemo(() => {
    let version = '';
    
    // First, check if there's a specific RU version for version 23
    if (patchOptions.ru && patchOptions.ruVersion && /^23(\.|$)/.test(patchOptions.ruVersion)) {
      // For version 23 with RU, use the specific RU version (e.g., 23.9, 23.15)
      version = patchOptions.ruVersion;
    } else if (targetVersion) {
      // Otherwise, use target version with default patch levels
      switch (targetVersion) {
        case '19':
          version = '19.30';
          break;
        case '21':
          version = '21.19';
          break;
        case '23':
        case '26':
          version = '23.26.1';
          break;
        default:
          version = '19.30';
      }
    } else {
      // Default to 19.30
      version = '19.30';
    }
    
    return isWindows 
      ? `C:\\Oracle\\Product\\${version}\\dbhome_1` 
      : `/u01/app/oracle/product/${version}/dbhome_1`;
  }, [targetVersion, patchOptions.ru, patchOptions.ruVersion, isWindows]);
  
  const targetHomePlaceholder = getTargetHomePlaceholder;

  // Function to generate dynamic target home path
  const generateDynamicTargetHome = React.useCallback(() => {
    if (sourceHome && sourceHome.trim() !== '' && sourceHome !== sourceHomePlaceholder) {
      // Extract path components from source home
      const sourcePath = sourceHome.replace(/\\/g, '/'); // Normalize to forward slashes
      const pathParts = sourcePath.split('/');
      
      // Find the version part (e.g., 19.15, 19.30)
      const versionIndex = pathParts.findIndex(part => /^\d+\.\d+$/.test(part));
      
      if (versionIndex !== -1) {
        // Replace version with placeholder
        const newPathParts = [...pathParts];
        newPathParts[versionIndex] = '%RELEASE%.%UPDATE%';
        
        // Reconstruct path
        let dynamicPath = newPathParts.join('/');
        
        // Convert back to Windows format if needed
        if (isWindows) {
          dynamicPath = dynamicPath.replace(/\//g, '\\');
        }
        
        return dynamicPath;
      }
    }
    
    // Default dynamic path if no source home or can't parse
    return isWindows 
      ? 'C:\\Oracle\\Product\\%RELEASE%.%UPDATE%\\dbhome_1'
      : '/u01/app/oracle/product/%RELEASE%.%UPDATE%/dbhome_1';
  }, [sourceHome, sourceHomePlaceholder, isWindows]);

  // Track previous path type to detect actual changes
  const prevPathTypeRef = React.useRef(pathType);
  
  // Handle path type changes without causing loops
  React.useEffect(() => {
    const prevPathType = prevPathTypeRef.current;
    
    // Only update if path type actually changed
    if (prevPathType !== pathType) {
      if (pathType === 'dynamic' && !targetHomeTouched) {
        const dynamicPath = generateDynamicTargetHome();
        setTargetHome(dynamicPath);
      } else if (pathType === 'dba_defined') {
        setTargetHome(targetHomePlaceholder);
      }
      
      // Update the ref to current value
      prevPathTypeRef.current = pathType;
    }
  }, [pathType, generateDynamicTargetHome, targetHomePlaceholder, setTargetHome, targetHomeTouched]);

  // For Download and Create Home modes, use <patchN> Log Dir (path: .../autoupgrade/log/<...>)
  const patchN = patchKey.toLowerCase();
  // For Analyze, Fixups, Deploy, use <SID> Log Directory (path: .../autoupgrade/log/<...>)
  const isUpgrade = patchKey.toLowerCase().startsWith('upg');
  const sidPart = isUpgrade ? `${sid || placeholders.sid}_Upgrade` : (sid || placeholders.sid);
  const logDirPlaceholderSid = isWindows
    ? `C:\\Oracle\\AutoUpgrade\\Log\\${sidPart}`
    : `/home/oracle/autoupgrade/log/${sidPart}`;
  const logDirPlaceholderPatchN = isWindows
    ? `C:\\Oracle\\AutoUpgrade\\Log\\${patchN}`
    : `/home/oracle/autoupgrade/log/${patchN}`;

  // Show logDir field in all modes, but position varies
  const showLogDirField = true;

  // Update oracleBase when platform or oracleBaseEnabled changes in create_home mode
  React.useEffect(() => {
    if (executionMode === 'create_home' && oracleBaseEnabled) {
      if (isWindows && (oracleBase === '' || oracleBase === placeholders.source_home || oracleBase === placeholders.target_home || oracleBase === '/u01/app/oracle')) {
        setOracleBase('C:\\Oracle');
      } else if (!isWindows && (oracleBase === '' || oracleBase === 'C:\\Oracle')) {
        setOracleBase('/u01/app/oracle');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, executionMode, oracleBaseEnabled]);

  // --- Central Validation for Create Home and Download ---
  const validationMessages = React.useMemo(() => {
    const ruVersionValid = typeof patchOptions.ruVersion === 'string' && /^\d+\.\d+$/.test(patchOptions.ruVersion.trim());
    let messages: { type: 'success' | 'warning'; content: React.ReactNode }[] = [];
    if (downloadMode && executionMode === 'create_home') {
      const hasTargetVersion = typeof targetVersionEnabled === 'boolean' ? targetVersionEnabled : false;
      const hasSourceHome = typeof sourceHomeEnabled === 'boolean' ? sourceHomeEnabled && ((!!sourceHome && sourceHome.trim() !== '') || (!sourceHome || sourceHome.trim() === '')) : false;
      const hasEdition = editionEnabled;
      const hasOracleBase = oracleBaseEnabled;
      const hasRU = patchOptions.ru && ruVersionValid;
      const showMainValidation = !hasSourceHome && !hasTargetVersion && !hasRU;
      const showBothEditionAndOracleBase = !hasSourceHome && (!hasEdition || !hasOracleBase);
      const showValidConfig = hasSourceHome || ((hasEdition && hasOracleBase) && (hasRU || hasTargetVersion));
      let errorLines: React.ReactNode[] = [];
      if (showMainValidation) {
        errorLines.push(
          <React.Fragment key="main">
            <Typography sx={{ fontWeight: 600, color: '#ff5252', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: 22, verticalAlign: 'middle' }}>⚠️</span>
              AutoUpgrade Patching could not derive the target version for prefix <b>{patchKey}</b>.
            </Typography>
            <Typography sx={{ fontSize: 15, color: '#ffbdbd', mb: 1 }}>Do one of the following:</Typography>
            <ol style={{ margin: 0, paddingLeft: 22, color: '#ffbdbd', fontSize: 15 }}>
              <li>Specify the <b>source_home</b> parameter</li>
              <li>Specify the <b>patch</b> parameter using <b>RU:x.y</b> notation</li>
              <li>Specify the <b>target_version</b> parameter</li>
            </ol>
          </React.Fragment>
        );
        errorLines.push(
          <Typography key="invalid" sx={{ fontWeight: 700, color: '#ff5252', mt: 2, pl: 2 }}>
            The current configuration is INVALID.
          </Typography>
        );
      } else if (showBothEditionAndOracleBase) {
        if (!hasEdition) {
          errorLines.push(
            <Typography key="edition" sx={{ fontSize: 15, color: '#ffbdbd', mt: 2 }}>
              <span style={{ fontWeight: 600, color: '#ff5252', marginRight: 6, fontSize: 18, verticalAlign: 'middle' }}>⚠️</span>
              AutoUpgrade Patching is unable to derive the edition that is needed to create the target ORACLE_HOME. Specify either the <b>source_home</b> or the <b>home_settings.edition</b> configuration parameter for prefix <b>{patchKey}</b>.
            </Typography>
          );
        }
        if (!hasOracleBase) {
          errorLines.push(
            <Typography key="oraclebase" sx={{ fontSize: 15, color: '#ffbdbd', mt: 2 }}>
              <span style={{ fontWeight: 600, color: '#ff5252', marginRight: 6, fontSize: 18, verticalAlign: 'middle' }}>⚠️</span>
              AutoUpgrade Patching is unable to derive the ORACLE_BASE value that is needed to create the target ORACLE_HOME. Specify either the <b>source_home</b> or the <b>home_settings.oracle_base</b> configuration parameter for prefix <b>{patchKey}</b>.
            </Typography>
          );
        }
        errorLines.push(
          <Typography key="invalid" sx={{ fontWeight: 700, color: '#ff5252', mt: 2, pl: 2 }}>
            The current configuration is INVALID.
          </Typography>
        );
      }
      if (errorLines.length > 0) {
        messages = errorLines.map(content => ({ type: 'warning', content }));
      } else if (showValidConfig) {
        messages = [{
          type: 'success',
          content: (
            <Box sx={{ mt: 2, p: 2.5, border: '2px solid #388e3c', borderRadius: 3, background: '#388e3c', color: '#fff', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <CheckCircleIcon sx={{ fontSize: 28, color: '#b9f6ca', mr: 1 }} />
              <Typography sx={{ fontWeight: 600, color: '#fff', fontSize: 19 }}>The current configuration is VALID.</Typography>
            </Box>
          )
        }];
      }
    } else if (downloadMode) {
      const hasSourceHome = typeof sourceHomeEnabled === 'boolean' ? sourceHomeEnabled && ((!!sourceHome && sourceHome.trim() !== '') || (!sourceHome || sourceHome.trim() === '')) : false;
      const hasTargetVersion = typeof targetVersionEnabled === 'boolean' ? targetVersionEnabled : false;
      const ruVersionValid = typeof patchOptions.ruVersion === 'string' && /^\d+\.\d+$/.test(patchOptions.ruVersion.trim());
      const hasRU = patchOptions.ru && ruVersionValid;
      const showMainValidation = !hasSourceHome && !hasTargetVersion && !hasRU;
      const showValidConfig = hasSourceHome || hasTargetVersion || hasRU;
      let errorLines: React.ReactNode[] = [];
      if (showMainValidation) {
        errorLines.push(
          <React.Fragment key="main">
            <Typography sx={{ fontWeight: 600, color: '#ff5252', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: 22, verticalAlign: 'middle' }}>⚠️</span>
              AutoUpgrade Patching could not derive the target version for prefix <b>{patchKey}</b>.
            </Typography>
            <Typography sx={{ fontSize: 15, color: '#ffbdbd', mb: 1 }}>Do one of the following:</Typography>
            <ol style={{ margin: 0, paddingLeft: 22, color: '#ffbdbd', fontSize: 15 }}>
              <li>Specify the <b>source_home</b> parameter</li>
              <li>Specify the <b>patch</b> parameter using <b>RU:x.y</b> notation</li>
              <li>Specify the <b>target_version</b> parameter</li>
            </ol>
          </React.Fragment>
        );
        errorLines.push(
          <Typography key="invalid" sx={{ fontWeight: 700, color: '#ff5252', mt: 2, pl: 2 }}>
            The current configuration is INVALID.
          </Typography>
        );
      }
      if (errorLines.length > 0) {
        messages = errorLines.map(content => ({ type: 'warning', content }));
      } else if (showValidConfig) {
        messages = [{
          type: 'success',
          content: (
            <Box sx={{ mt: 2, p: 2.5, border: '2px solid #388e3c', borderRadius: 3, background: '#388e3c', color: '#fff', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <CheckCircleIcon sx={{ fontSize: 28, color: '#b9f6ca', mr: 1 }} />
              <Typography sx={{ fontWeight: 600, color: '#fff', fontSize: 19 }}>The current configuration is VALID.</Typography>
            </Box>
          )
        }];
      }
    }
    return messages;
  }, [downloadMode, executionMode, patchKey, editionEnabled, oracleBaseEnabled, patchOptions, targetVersionEnabled, sourceHomeEnabled, sourceHome]);

  React.useEffect(() => {
    if (renderValidationMessages) {
      renderValidationMessages(validationMessages);
    }
  }, [renderValidationMessages, validationMessages]);

  const handleSidChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSidTouched(true);
    if (!sidTouched && sid === placeholders.sid) {
      setSid(e.target.value);
    } else {
      setSid(e.target.value);
    }
  };
  const handleSidFocus = () => {
    if (!sidTouched && sid === placeholders.sid) {
      setSid('');
    }
    setSidTouched(true);
  };

  const handleSourceHomeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSourceHomeTouched(true);
    if (!sourceHomeTouched && sourceHome === placeholders.source_home) {
      setSourceHome(e.target.value);
    } else {
      setSourceHome(e.target.value);
    }
  };
  const handleSourceHomeFocus = () => {
    if (!sourceHomeTouched && sourceHome === placeholders.source_home) {
      setSourceHome('');
    }
    setSourceHomeTouched(true);
  };

  const handleTargetHomeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setTargetHome(newValue);
    setTargetHomeTouched(true);
  };
  const handleTargetHomeFocus = () => {
    setTargetHomeTouched(true);
  };

  // Target Version switch state (local only for download mode)
  const [internalTargetVersionEnabled, setInternalTargetVersionEnabled] = React.useState(false);
  const effectiveTargetVersionEnabled = typeof targetVersionEnabled === 'boolean' ? targetVersionEnabled : internalTargetVersionEnabled;
  const effectiveSetTargetVersionEnabled = setTargetVersionEnabled || setInternalTargetVersionEnabled;

  // Download Mode: Source Home switch state
  const [internalSourceHomeEnabled, setInternalSourceHomeEnabled] = React.useState(false);
  const effectiveSourceHomeEnabled = typeof sourceHomeEnabled === 'boolean' ? sourceHomeEnabled : internalSourceHomeEnabled;
  const effectiveSetSourceHomeEnabled = setSourceHomeEnabled || setInternalSourceHomeEnabled;

  // Auto-switch edition from SE2 to EE when target version 23 or 26 is selected (SE2 not available)
  React.useEffect(() => {
    if ((targetVersion === '23' || targetVersion === '26') && edition === 'se2') {
      setEdition('ee');
    }
  }, [targetVersion, edition, setEdition]);

  // Add state for Oracle Base switch and value
  const [oracleBaseTouched, setOracleBaseTouched] = React.useState(false);

  // --- CREATE_HOME MODE UI ---
  if (downloadMode && executionMode === 'create_home') {
    return (
      <Paper elevation={0} sx={{ p: 1, background: 'background.paper', borderRadius: 3 }}>
        {/* Source Home, Oracle Base, Edition, and Microsoft Windows Switches (Create Home mode) */}
        {executionMode === 'create_home' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {/* Source Home Switch and Textbox */}
            <Tooltip
              title="Specify the Source Oracle Home from which AutoUpgrade patching can derive the target version."
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                    color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                    fontSize: 15,
                    borderRadius: 2,
                    boxShadow: 3,
                    px: 2,
                    py: 1,
                    fontWeight: 400,
                    minWidth: 320,
                  },
                },
              }}
            >
              <FormControlLabel
                sx={{ width: 'fit-content' }}
                control={
                  <Switch
                    checked={effectiveSourceHomeEnabled}
                    onChange={e => effectiveSetSourceHomeEnabled(e.target.checked)}
                    color="primary"
                  />
                }
                label={<Typography sx={{ fontWeight: 600, fontSize: 16 }}>Source Home</Typography>}
              />
            </Tooltip>
            {effectiveSourceHomeEnabled && (
              <TextField
                fullWidth
                size="small"
                value={sourceHomeTouched || sourceHome ? sourceHome : sourceHomePlaceholder}
                onChange={handleSourceHomeChange}
                onFocus={handleSourceHomeFocus}
                placeholder={sourceHomePlaceholder}
                sx={{ mt: 1 }}
                InputProps={{
                  sx: {
                    '&::placeholder': { color: '#8a8a8a', opacity: 1 },
                    '& .MuiInputBase-input': {
                      color:
                        !sourceHomeTouched && (sourceHome === '' || sourceHome === sourceHomePlaceholder)
                          ? '#8a8a8a'
                          : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                    },
                    fontSize: 15,
                  },
                }}
              />
            )}
            {/* Oracle Base Switch and Textbox */}
            <Tooltip
              title="Specifies the directory that should be used for the Oracle base directory."
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                    color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                    fontSize: 15,
                    borderRadius: 2,
                    boxShadow: 3,
                    px: 2,
                    py: 1,
                    fontWeight: 400,
                    minWidth: 320,
                  },
                },
              }}
            >
              <FormControlLabel
                sx={{ width: 'fit-content' }}
                control={
                  <Switch
                    checked={oracleBaseEnabled}
                    onChange={e => setOracleBaseEnabled(e.target.checked)}
                    color="primary"
                  />
                }
                label={<Typography sx={{ fontWeight: 600, fontSize: 16 }}>Oracle Base</Typography>}
              />
            </Tooltip>
            {oracleBaseEnabled && (
              <TextField
                fullWidth
                size="small"
                value={oracleBaseTouched ? oracleBase : oracleBase || (isWindows ? 'C:\\Oracle' : '/u01/app/oracle')}
                onChange={e => {
                  setOracleBaseTouched(true);
                  setOracleBase(e.target.value);
                }}
                onFocus={() => {
                  if (!oracleBaseTouched && (oracleBase === '/u01/app/oracle' || oracleBase === 'C:\\Oracle')) {
                    setOracleBase('');
                  }
                  setOracleBaseTouched(true);
                }}
                placeholder={isWindows ? 'C:\\Oracle' : '/u01/app/oracle'}
                sx={{ width: '100%' }}
                InputProps={{
                  sx: {
                    '&::placeholder': { color: '#8a8a8a', opacity: 1 },
                    '& .MuiInputBase-input': {
                      color:
                        !oracleBaseTouched && (oracleBase === '' || oracleBase === '/u01/app/oracle' || oracleBase === 'C:\\Oracle')
                          ? '#8a8a8a'
                          : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                    },
                    fontSize: 15,
                  },
                }}
              />
            )}
            {/* Edition Switch and RadioGroup */}
            <Tooltip
              title="Specifies the Oracle Database edition that you want to use for creating the target ORACLE_HOME."
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                    color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                    fontSize: 15,
                    borderRadius: 2,
                    boxShadow: 3,
                    px: 2,
                    py: 1,
                    fontWeight: 400,
                    minWidth: 320,
                  },
                },
              }}
            >
              <FormControlLabel
                sx={{ width: 'fit-content' }}
                control={
                  <Switch
                    checked={editionEnabled}
                    onChange={e => setEditionEnabled(e.target.checked)}
                    color="primary"
                  />
                }
                label={<Typography sx={{ fontWeight: 600, fontSize: 16 }}>Edition</Typography>}
              />
            </Tooltip>
            {editionEnabled && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: 42 }} />
                <RadioGroup
                  row
                  value={edition}
                  onChange={e => setEdition(e.target.value)}
                >
                  <FormControlLabel value="se2" disabled={targetVersion === '23' || targetVersion === '26'} control={<Radio size="small" />} label="Standard Edition 2" />
                  <FormControlLabel value="ee" control={<Radio size="small" />} label="Enterprise Edition" />
                </RadioGroup>
              </Box>
            )}
            {/* oraInventory and Inventory Group Switchers (Linux/Unix only) */}
            {!isWindows && (
              <>
                {/* oraInventory Switch and Textbox */}
                <Tooltip
                  title="Specifies the directory that should be used for the Oracle Database inventory (oraInventory) directory. Required when creating a new Oracle Home on a machine with no existing Oracle Inventory. If the inventory does not exist, this directory will be created."
                  placement="right"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: (theme: any) => ({
                        bgcolor: theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                        color: theme.palette.mode === 'dark' ? '#222' : '#fff',
                        fontSize: 15,
                        borderRadius: 2,
                        boxShadow: 3,
                        px: 2,
                        py: 1,
                        fontWeight: 400,
                        minWidth: 320,
                      }),
                    },
                  }}
                >
                  <FormControlLabel
                    sx={{ width: 'fit-content' }}
                    control={
                      <Switch
                        checked={oraInventoryEnabled}
                        onChange={e => setOraInventoryEnabled(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={<Typography sx={{ fontWeight: 600, fontSize: 16 }}>oraInventory</Typography>}
                  />
                </Tooltip>
                {oraInventoryEnabled && (
                  <TextField
                    fullWidth
                    size="small"
                    value={oraInventory}
                    onChange={e => setOraInventory(e.target.value)}
                    placeholder={'/u01/app/oraInventory'}
                    sx={{ mt: 1 }}
                    InputProps={{
                      sx: {
                        '&::placeholder': { color: '#8a8a8a', opacity: 1 },
                        '& .MuiInputBase-input': {
                          color:
                            oraInventory === ''
                              ? '#8a8a8a'
                              : (theme: any) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                        },
                        fontSize: 15,
                      },
                    }}
                  />
                )}
                {/* Inventory Group Switch and Textbox */}
                <Tooltip
                  title="Members of the Oracle Inventory group are granted OINSTALL privileges to read and write to the Oracle Inventory directory (oraInventory). Oracle software owners are members of this group. When oraInst.loc already exists on the system, the default value matches the specified operating system group that is already defined on the system. Otherwise, the default value is oinstall."
                  placement="right"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: (theme: any) => ({
                        bgcolor: theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                        color: theme.palette.mode === 'dark' ? '#222' : '#fff',
                        fontSize: 15,
                        borderRadius: 2,
                        boxShadow: 3,
                        px: 2,
                        py: 1,
                        fontWeight: 400,
                        minWidth: 320,
                      }),
                    },
                  }}
                >
                  <FormControlLabel
                    sx={{ width: 'fit-content' }}
                    control={
                      <Switch
                        checked={inventoryGroupEnabled}
                        onChange={e => setInventoryGroupEnabled(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={<Typography sx={{ fontWeight: 600, fontSize: 16 }}>Inventory Group</Typography>}
                  />
                </Tooltip>
                {inventoryGroupEnabled && (
                  <TextField
                    fullWidth
                    size="small"
                    value={inventoryGroup}
                    onChange={e => setInventoryGroup(e.target.value)}
                    placeholder={'oinstall'}
                    sx={{ mt: 1 }}
                    InputProps={{
                      sx: {
                        '&::placeholder': { color: '#8a8a8a', opacity: 1 },
                        '& .MuiInputBase-input': {
                          color:
                            inventoryGroup === ''
                              ? '#8a8a8a'
                              : (theme: any) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                        },
                        fontSize: 15,
                      },
                    }}
                  />
                )}
              </>
            )}
            {/* Microsoft Windows Switch (tooltip only in Create Home mode) */}
            <Tooltip
              title="Change paths for Windows standards."
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                    color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                    fontSize: 15,
                    borderRadius: 2,
                    boxShadow: 3,
                    px: 2,
                    py: 1,
                    fontWeight: 400,
                    minWidth: 220,
                  },
                },
              }}
            >
              <FormControlLabel
                sx={{ width: 'fit-content' }}
                control={
                  <Switch
                    checked={platform === 'WINDOWS.X64'}
                    onChange={e => setPlatform(e.target.checked ? 'WINDOWS.X64' : 'LINUX.X64')}
                    color="primary"
                  />
                }
                label={<Typography sx={{ fontWeight: 600, fontSize: 16 }}>Microsoft Windows</Typography>}
              />
            </Tooltip>
            {/* Ignore PreReq Failure Switch (Create Home mode only) */}
            <Tooltip
              title="When enabled, AutoUpgrade will ignore prerequisite check failures during home creation."
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                    color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                    fontSize: 15,
                    borderRadius: 2,
                    boxShadow: 3,
                    px: 2,
                    py: 1,
                    fontWeight: 400,
                    minWidth: 320,
                  },
                },
              }}
            >
              <FormControlLabel
                sx={{ width: 'fit-content' }}
                control={
                  <Switch
                    checked={!!ignorePrereqFailure}
                    onChange={e => {
                      if (setIgnorePrereqFailure) {
                        setIgnorePrereqFailure(e.target.checked);
                      }
                    }}
                    color="primary"
                  />
                }
                label={<Typography sx={{ fontWeight: 600, fontSize: 16 }}>Ignore PreReq Failure</Typography>}
              />
            </Tooltip>
          </Box>
        )}
        {/* Log Directory Field (above Target Home) */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>{patchLabel || patchN} Log Dir:</Typography>
          <TextField
            fullWidth
            size="small"
            value={logDir}
            onChange={e => { setLogDirTouched(true); setLogDir(e.target.value); }}
            onFocus={() => { if (!logDirTouched && logDir === '') setLogDir(''); setLogDirTouched(true); }}
            placeholder={logDirPlaceholderPatchN}
            InputProps={{
              sx: {
                '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                '& .MuiInputBase-input': {
                  color: !logDirTouched && logDir === '' 
                    ? '#8a8a8a' 
                    : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
                fontSize: 15,
              },
            }}
          />
        </Box>
        {/* Target Home */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Target Home:</Typography>
          
          {/* Path Type Radio Buttons */}
          <Box sx={{ mb: 2 }}>
            <RadioGroup
              row
              value={pathType}
              onChange={(e) => setPathType(e.target.value as 'dynamic' | 'dba_defined')}
              sx={{ '& .MuiFormControlLabel-root': { mr: 3 } }}
            >
              <FormControlLabel
                value="dynamic"
                control={<Radio size="small" />}
                label={
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                    Dynamic Path
                  </Typography>
                }
              />
              <FormControlLabel
                value="dba_defined"
                control={<Radio size="small" />}
                label={
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                    DBA defined path
                  </Typography>
                }
              />
            </RadioGroup>
          </Box>

          <TextField
            fullWidth
            size="small"
            value={targetHome}
            onChange={handleTargetHomeChange}
            onFocus={handleTargetHomeFocus}
            placeholder={targetHomePlaceholder}
            sx={{ 
              '& .MuiInputBase-root': { 
                backgroundColor: 'transparent',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.04)' }
              }
            }}
            InputProps={{
              sx: {
                '&::placeholder': { color: '#8a8a8a', opacity: 1 },
                '& .MuiInputBase-input': {
                  color: '#b0b0b0',
                  cursor: 'text',
                  userSelect: 'text',
                  pointerEvents: 'auto',
                  WebkitUserSelect: 'text',
                  MozUserSelect: 'text',
                  msUserSelect: 'text',
                },
                fontSize: 15,
              },
            }}
          />
          
          {pathType === 'dynamic' && (
            <Typography sx={{ fontSize: 12, color: '#666', mt: 1, fontStyle: 'italic' }}>
              Dynamic path will be automatically generated based on source home or default pattern. You can edit this path if needed.
            </Typography>
          )}
          {pathType === 'dba_defined' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Typography sx={{ fontSize: 12, color: '#666', fontStyle: 'italic' }}>
                DBA defined path mode. You can manually edit the path or
              </Typography>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setTargetHome(targetHomePlaceholder);
                  setTargetHomeTouched(false);
                }}
                sx={{ 
                  fontSize: 11, 
                  color: '#4a9eff', 
                  textTransform: 'none',
                  p: 0,
                  minWidth: 'auto'
                }}
              >
                restore default
              </Button>
            </Box>
          )}
        </Box>
        {/* Target Version Switch */}
        <Box sx={{ mb: 2 }}>
          <Tooltip
            title="For AutoUpgrade patching, specify the target release version on which you want AutoUpgrade to perform the patch maintenance operation."
            placement="right"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                  color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                  fontSize: 15,
                  borderRadius: 2,
                  boxShadow: 3,
                  px: 2,
                  py: 1,
                  fontWeight: 400,
                  minWidth: 320,
                },
              },
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={effectiveTargetVersionEnabled}
                  onChange={e => effectiveSetTargetVersionEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label={<Typography sx={{ fontWeight: 600 }}>Target Version</Typography>}
            />
          </Tooltip>
          {effectiveTargetVersionEnabled && (
            <Select
              size="small"
              value={targetVersion || '19'}
              onChange={e => setTargetVersion && setTargetVersion(e.target.value as string)}
              sx={{ width: 80, ml: 2 }}
            >
              <MenuItem value="19">19</MenuItem>
              <MenuItem value="21">21</MenuItem>
              <MenuItem value="23">23</MenuItem>
              <MenuItem value="26">26</MenuItem>
            </Select>
          )}
        </Box>
        {/* Windows Credential File UI: Only for Analyze, Fixups, Deploy */}
        {['analyze', 'fixups', 'deploy'].includes(executionMode ?? '') && platform === 'WINDOWS.X64' && (
          <>
            <Box sx={{ mb: 2 }}>
              <Tooltip
                title="Specifies the location of a Microsoft Windows credential object file that AutoUpgrade will use to store the user credentials."
                placement="right"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                      color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                      fontSize: 15,
                      borderRadius: 2,
                      boxShadow: 3,
                      px: 2,
                      py: 1,
                      fontWeight: 400,
                      minWidth: 320,
                    },
                  },
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>Windows Credential File:</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={winCredential}
                    onChange={e => setWinCredential(e.target.value)}
                    placeholder={`C:\\Oracle\\AutoUpgrade\\Credential\\${patchKey}.xml`}
                    InputProps={{
                      sx: {
                        '&::placeholder': { color: '#8a8a8a', opacity: 1 },
                        '& .MuiInputBase-input': {
                          color: theme => theme.palette.mode === 'dark' ? '#6c757d' : '#000',
                        },
                        fontSize: 15,
                      },
                    }}
                  />
                </Box>
              </Tooltip>
            </Box>
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={deleteWinCredentialFile}
                    onChange={e => setDeleteWinCredentialFile(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Tooltip
                    title="Deletes the Microsoft Windows credential object file when the AutoUpgrade job is complete."
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                          color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                          fontSize: 15,
                          borderRadius: 2,
                          boxShadow: 3,
                          px: 2,
                          py: 1,
                          fontWeight: 400,
                          minWidth: 320,
                        },
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>Delete Windows Credential File</Typography>
                  </Tooltip>
                }
              />
            </Box>
          </>
        )}
      </Paper>
    );
  }

  // --- DOWNLOAD MODE UI ---
  if (downloadMode) {
    return (
      <Paper elevation={0} sx={{ p: 1, background: 'background.paper', borderRadius: 3 }}>
        {/* Source Home Switch */}
        <Box sx={{ mb: 2 }}>
          <Tooltip
            title="Specify the Source Oracle Home from which AutoUpgrade patching can derive the target version."
            placement="right"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                  color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                  fontSize: 15,
                  borderRadius: 2,
                  boxShadow: 3,
                  px: 2,
                  py: 1,
                  fontWeight: 400,
                  minWidth: 320,
                },
              },
            }}
          >
            <FormControlLabel
              sx={{ width: 'fit-content' }}
              control={
                <Switch
                  checked={effectiveSourceHomeEnabled}
                  onChange={e => effectiveSetSourceHomeEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label={<Typography sx={{ fontWeight: 600, fontSize: 16 }}>Source Home</Typography>}
            />
          </Tooltip>
          {effectiveSourceHomeEnabled && (
            <TextField
              fullWidth
              size="small"
              value={sourceHomeTouched || sourceHome ? sourceHome : sourceHomePlaceholder}
              onChange={handleSourceHomeChange}
              onFocus={handleSourceHomeFocus}
              placeholder={sourceHomePlaceholder}
              sx={{ mt: 1 }}
              InputProps={{
                sx: {
                  '&::placeholder': { color: '#8a8a8a', opacity: 1 },
                  '& .MuiInputBase-input': {
                    color:
                      !sourceHomeTouched && (sourceHome === '' || sourceHome === sourceHomePlaceholder)
                        ? '#8a8a8a'
                        : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                  },
                  fontSize: 15,
                },
              }}
            />
          )}
        </Box>
        {/* Target Version Switch */}
        <Box sx={{ mb: 2 }}>
          <Tooltip
            title="For AutoUpgrade patching, specify the target release version on which you want AutoUpgrade to perform the patch maintenance operation."
            placement="right"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                  color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                  fontSize: 15,
                  borderRadius: 2,
                  boxShadow: 3,
                  px: 2,
                  py: 1,
                  fontWeight: 400,
                  minWidth: 320,
                },
              },
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={effectiveTargetVersionEnabled}
                  onChange={e => effectiveSetTargetVersionEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label={<Typography sx={{ fontWeight: 600 }}>Target Version</Typography>}
            />
          </Tooltip>
          {effectiveTargetVersionEnabled && (
            <Select
              size="small"
              value={targetVersion || '19'}
              onChange={e => setTargetVersion && setTargetVersion(e.target.value as string)}
              sx={{ width: 80, ml: 2 }}
            >
              <MenuItem value="19">19</MenuItem>
              <MenuItem value="21">21</MenuItem>
              <MenuItem value="23">23</MenuItem>
              <MenuItem value="26">26</MenuItem>
            </Select>
          )}
        </Box>
        {/* Log Directory Field (between Target Version and Platform) */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>{patchLabel || patchN} Log Dir:</Typography>
          <TextField
            fullWidth
            size="small"
            value={logDir}
            onChange={e => { setLogDirTouched(true); setLogDir(e.target.value); }}
            onFocus={() => { if (!logDirTouched && logDir === '') setLogDir(''); setLogDirTouched(true); }}
            placeholder={logDirPlaceholderPatchN}
            InputProps={{
              sx: {
                '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                '& .MuiInputBase-input': {
                  color: !logDirTouched && logDir === '' 
                    ? '#8a8a8a' 
                    : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
                fontSize: 15,
              },
            }}
          />
        </Box>
        {/* Platform Dropdown */}
        <Box sx={{ mb: 2 }}>
          <Tooltip
            title="Specify the target platform for which you want to download patches. For example, you may be using Microsoft Windows on your workstation but can select Linux to download patches for Linux."
            placement="right"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                  color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                  fontSize: 15,
                  borderRadius: 2,
                  boxShadow: 3,
                  px: 2,
                  py: 1,
                  fontWeight: 400,
                  minWidth: 320,
                },
              },
            }}
          >
            <span>
              <Typography sx={{ fontWeight: 600, mb: 1, display: 'inline-block' }}>Platform:</Typography>
            </span>
          </Tooltip>
          <Tooltip
            title="Specify the target platform for which you want to download patches. For example, you may be using Microsoft Windows on your workstation but can select Linux to download patches for Linux."
            placement="right"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                  color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                  fontSize: 15,
                  borderRadius: 2,
                  boxShadow: 3,
                  px: 2,
                  py: 1,
                  fontWeight: 400,
                  minWidth: 320,
                },
              },
            }}
          >
            <Select
              value={platform}
              onChange={e => setPlatform(e.target.value)}
              size="small"
              fullWidth
              displayEmpty
              sx={{ fontSize: 15, color: platform === 'LINUX.X64' ? '#b0b0b0' : theme.palette.text.primary }}
            >
              {PLATFORM_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Tooltip>
        </Box>
        {/* Keystore Textbox */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Keystore:</Typography>
          <TextField
            fullWidth
            size="small"
            value={keystorePath}
            onChange={e => setKeystorePath(e.target.value)}
            placeholder="Enter keystore path"
            sx={{ mt: 1 }}
          />
        </Box>
      </Paper>
    );
  }

  // --- ANALYZE, FIXUPS, DEPLOY UI ---
  // Use <SID> Log Directory and SID in placeholder
  if (['analyze', 'fixups', 'deploy'].includes(executionMode ?? '')) {
    return (
      <Paper elevation={0} sx={{ p: 1, background: 'background.paper', borderRadius: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>SID:</Typography>
          <TextField
            fullWidth
            size="small"
            value={sidTouched || sid ? sid : ''}
            onChange={handleSidChange}
            onFocus={handleSidFocus}
            placeholder={placeholders.sid}
            InputProps={{
              sx: {
                '&::placeholder': { color: '#8a8a8a', opacity: 1 },
                '& .MuiInputBase-input': {
                  color:
                    !sidTouched && (sid === '' || sid === placeholders.sid)
                      ? '#8a8a8a'
                      : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
                fontSize: 15,
              },
            }}
          />
        </Box>
        {/* Log Directory Field (below SID) */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>{sid || placeholders.sid} Log Directory:</Typography>
          <TextField
            fullWidth
            size="small"
            value={logDirTouched || logDir ? logDir : ''}
            onChange={e => { setLogDirTouched(true); setLogDir(e.target.value); }}
            onFocus={() => { if (!logDirTouched && logDir === '') setLogDir(''); setLogDirTouched(true); }}
            placeholder={logDirPlaceholderSid}
            InputProps={{
              sx: {
                '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                '& .MuiInputBase-input': {
                  color: !logDirTouched && logDir === '' 
                    ? '#8a8a8a' 
                    : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
                fontSize: 15,
              },
            }}
          />
        </Box>
        {/* Source Home */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Source Home:</Typography>
          <TextField
            fullWidth
            size="small"
            value={sourceHomeTouched || sourceHome ? sourceHome : sourceHomePlaceholder}
            onChange={handleSourceHomeChange}
            onFocus={handleSourceHomeFocus}
            placeholder={sourceHomePlaceholder}
            InputProps={{
              sx: {
                '&::placeholder': { color: '#8a8a8a', opacity: 1 },
                '& .MuiInputBase-input': {
                  color:
                    !sourceHomeTouched && (sourceHome === '' || sourceHome === sourceHomePlaceholder)
                      ? '#8a8a8a'
                      : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
                fontSize: 15,
              },
            }}
          />
        </Box>
        {/* Target Home */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Target Home:</Typography>
          
          {/* Path Type Radio Buttons */}
          <Box sx={{ mb: 2 }}>
            <RadioGroup
              row
              value={pathType}
              onChange={(e) => setPathType(e.target.value as 'dynamic' | 'dba_defined')}
              sx={{ '& .MuiFormControlLabel-root': { mr: 3 } }}
            >
              <FormControlLabel
                value="dynamic"
                control={<Radio size="small" />}
                label={
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                    Dynamic Path
                  </Typography>
                }
              />
              <FormControlLabel
                value="dba_defined"
                control={<Radio size="small" />}
                label={
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                    DBA defined path
                  </Typography>
                }
              />
            </RadioGroup>
          </Box>

          <TextField
            fullWidth
            size="small"
            value={targetHome}
            onChange={handleTargetHomeChange}
            onFocus={handleTargetHomeFocus}
            placeholder={targetHomePlaceholder}
            InputProps={{
              sx: {
                '&::placeholder': { color: '#8a8a8a', opacity: 1 },
                '& .MuiInputBase-input': {
                  color: '#b0b0b0',
                },
                fontSize: 15,
              },
            }}
          />
          
          {pathType === 'dynamic' && (
            <Typography sx={{ fontSize: 12, color: '#666', mt: 1, fontStyle: 'italic' }}>
              Dynamic path will be automatically generated based on source home or default pattern. You can edit this path if needed.
            </Typography>
          )}
        </Box>
        {/* Ignore PreReq Failure Switch (Analyze and Deploy modes only) */}
        {(executionMode === 'analyze' || executionMode === 'deploy') && (
          <Box sx={{ mb: 2 }}>
            <Tooltip
              title="When enabled, AutoUpgrade will ignore prerequisite check failures during patching operations."
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                    color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                    fontSize: 15,
                    borderRadius: 2,
                    boxShadow: 3,
                    px: 2,
                    py: 1,
                    fontWeight: 400,
                    minWidth: 320,
                  },
                },
              }}
            >
              <FormControlLabel
                sx={{ width: 'fit-content' }}
                control={
                  <Switch
                    checked={!!ignorePrereqFailure}
                    onChange={e => {
                      if (setIgnorePrereqFailure) {
                        setIgnorePrereqFailure(e.target.checked);
                      }
                    }}
                    color="primary"
                  />
                }
                label={<Typography sx={{ fontWeight: 600, fontSize: 16 }}>Ignore PreReq Failure</Typography>}
              />
            </Tooltip>
          </Box>
        )}
        {/* Microsoft Windows Switch */}
        <Tooltip
          title="Enable to specify a Windows Credential File for AutoUpgrade."
          placement="right"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                fontSize: 15,
                borderRadius: 2,
                boxShadow: 3,
                px: 2,
                py: 1,
                fontWeight: 400,
                minWidth: 320,
              },
            },
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={platform === 'WINDOWS.X64'}
                onChange={e => setPlatform(e.target.checked ? 'WINDOWS.X64' : 'LINUX.X64')}
                color="primary"
              />
            }
            label={<Typography sx={{ fontWeight: 600 }}>Microsoft Windows</Typography>}
          />
        </Tooltip>
        {/* End Microsoft Windows Switch for Analyze/Fixups/Deploy */}
        {/* Windows Credential File UI: Only for Analyze, Fixups, Deploy */}
        {platform === 'WINDOWS.X64' && (
          <>
            <Box sx={{ mb: 2 }}>
              <Tooltip
                title="Specifies the location of a Microsoft Windows credential object file that AutoUpgrade will use to store the user credentials."
                placement="right"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                      color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                      fontSize: 15,
                      borderRadius: 2,
                      boxShadow: 3,
                      px: 2,
                      py: 1,
                      fontWeight: 400,
                      minWidth: 320,
                    },
                  },
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>Windows Credential File:</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={winCredential}
                    onChange={e => setWinCredential(e.target.value)}
                    placeholder={`C:\\Oracle\\AutoUpgrade\\Credential\\${patchKey}.xml`}
                    InputProps={{
                      sx: {
                        '&::placeholder': { color: '#8a8a8a', opacity: 1 },
                        '& .MuiInputBase-input': {
                          color: theme => theme.palette.mode === 'dark' ? '#6c757d' : '#000',
                        },
                        fontSize: 15,
                      },
                    }}
                  />
                </Box>
              </Tooltip>
            </Box>
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={deleteWinCredentialFile}
                    onChange={e => setDeleteWinCredentialFile(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Tooltip
                    title="Deletes the Microsoft Windows credential object file when the AutoUpgrade job is complete."
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                          color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                          fontSize: 15,
                          borderRadius: 2,
                          boxShadow: 3,
                          px: 2,
                          py: 1,
                          fontWeight: 400,
                          minWidth: 320,
                        },
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>Delete Windows Credential File</Typography>
                  </Tooltip>
                }
              />
            </Box>
          </>
        )}
        {/* Target Version Switch */}
        <Box sx={{ mb: 2, mt: 1 }}>
          <Tooltip
            title="For AutoUpgrade patching, specify the target release version on which you want AutoUpgrade to perform the patch maintenance operation."
            placement="right"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                  color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                  fontSize: 15,
                  borderRadius: 2,
                  boxShadow: 3,
                  px: 2,
                  py: 1,
                  fontWeight: 400,
                  minWidth: 320,
                },
              },
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={effectiveTargetVersionEnabled}
                  onChange={e => effectiveSetTargetVersionEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label={<Typography sx={{ fontWeight: 600 }}>Target Version</Typography>}
            />
          </Tooltip>
          {effectiveTargetVersionEnabled && (
            <Select
              size="small"
              value={targetVersion || '19'}
              onChange={e => setTargetVersion && setTargetVersion(e.target.value as string)}
              sx={{ width: 80, ml: 2 }}
            >
              <MenuItem value="19">19</MenuItem>
              <MenuItem value="21">21</MenuItem>
              <MenuItem value="23">23</MenuItem>
              <MenuItem value="26">26</MenuItem>
            </Select>
          )}
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 1, background: 'background.paper', borderRadius: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>SID:</Typography>
        <TextField
          fullWidth
          size="small"
          value={sidTouched || sid ? sid : ''}
          onChange={handleSidChange}
          onFocus={handleSidFocus}
          placeholder={placeholders.sid}
          InputProps={{
            sx: {
              '&::placeholder': { color: '#8a8a8a', opacity: 1 },
              '& .MuiInputBase-input': {
                color:
                  !sidTouched && (sid === '' || sid === placeholders.sid)
                    ? '#8a8a8a'
                    : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
              },
              fontSize: 15,
            },
          }}
        />
      </Box>
      {/* Log Directory Field (positioned as required) */}
      {showLogDirField && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>{patchLabel || patchN} Log Dir:</Typography>
          <TextField
            fullWidth
            size="small"
            value={logDirTouched || logDir ? logDir : ''}
            onChange={e => { setLogDirTouched(true); setLogDir(e.target.value); }}
            onFocus={() => { if (!logDirTouched && logDir === '') setLogDir(''); setLogDirTouched(true); }}
            placeholder={logDirPlaceholderPatchN}
            InputProps={{
              sx: {
                '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
                '& .MuiInputBase-input': {
                  color: !logDirTouched && logDir === '' 
                    ? '#8a8a8a' 
                    : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
                fontSize: 15,
              },
            }}
          />
        </Box>
      )}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>Source Home:</Typography>
        <TextField
          fullWidth
          size="small"
          value={sourceHomeTouched || sourceHome ? sourceHome : sourceHomePlaceholder}
          onChange={handleSourceHomeChange}
          onFocus={handleSourceHomeFocus}
          placeholder={sourceHomePlaceholder}
          InputProps={{
            sx: {
              '&::placeholder': { color: '#8a8a8a', opacity: 1 },
              '& .MuiInputBase-input': {
                color:
                  !sourceHomeTouched && (sourceHome === '' || sourceHome === sourceHomePlaceholder)
                    ? '#8a8a8a'
                    : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
              },
              fontSize: 15,
            },
          }}
        />
      </Box>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>Target Home:</Typography>
        
        {/* Path Type Radio Buttons */}
        <Box sx={{ mb: 2 }}>
          <RadioGroup
            row
            value={pathType}
            onChange={(e) => setPathType(e.target.value as 'dynamic' | 'dba_defined')}
            sx={{ '& .MuiFormControlLabel-root': { mr: 3 } }}
          >
            <FormControlLabel
              value="dynamic"
              control={<Radio size="small" />}
              label={
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                  Dynamic Path
                </Typography>
              }
            />
            <FormControlLabel
              value="dba_defined"
              control={<Radio size="small" />}
              label={
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                  DBA defined path
                </Typography>
              }
            />
          </RadioGroup>
        </Box>

                                     <TextField
             fullWidth
             size="small"
             value={targetHome}
             onChange={handleTargetHomeChange}
             onFocus={handleTargetHomeFocus}
             placeholder={targetHomePlaceholder}
             InputProps={{
               sx: {
                 '&::placeholder': { color: '#8a8a8a', opacity: 1 },
                 '& .MuiInputBase-input': {
                   color: '#b0b0b0',
                 },
                 fontSize: 15,
               },
             }}
           />
        
        {pathType === 'dynamic' && (
          <Typography sx={{ fontSize: 12, color: '#666', mt: 1, fontStyle: 'italic' }}>
            Dynamic path will be automatically generated based on source home or default pattern. You can edit this path if needed.
          </Typography>
        )}
      </Box>
      <Box>
        <Box sx={{ display: 'none' }}>
          <Select
            value={platform}
            onChange={e => setPlatform(e.target.value)}
            size="small"
            fullWidth
            displayEmpty
            sx={{ fontSize: 15, color: platform === 'LINUX.X64' ? '#b0b0b0' : theme.palette.text.primary }}
          >
            {PLATFORM_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
        {/* Microsoft Windows Switch */}
        {['analyze', 'fixups', 'deploy'].includes(executionMode ?? '') && (
          <Tooltip
            title="Enable to specify a Windows Credential File for AutoUpgrade."
            placement="right"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                  color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                  fontSize: 15,
                  borderRadius: 2,
                  boxShadow: 3,
                  px: 2,
                  py: 1,
                  fontWeight: 400,
                  minWidth: 320,
                },
              },
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={platform === 'WINDOWS.X64'}
                  onChange={e => setPlatform(e.target.checked ? 'WINDOWS.X64' : 'LINUX.X64')}
                  color="primary"
                />
              }
              label={<Typography sx={{ fontWeight: 600 }}>Microsoft Windows</Typography>}
            />
          </Tooltip>
        )}
        {/* End Microsoft Windows Switch for Analyze/Fixups/Deploy */}
        {/* Windows Credential File UI: Only for Analyze, Fixups, Deploy */}
        {['analyze', 'fixups', 'deploy'].includes(executionMode ?? '') && platform === 'WINDOWS.X64' && (
          <>
            <Box sx={{ mb: 2 }}>
              <Tooltip
                title="Specifies the location of a Microsoft Windows credential object file that AutoUpgrade will use to store the user credentials."
                placement="right"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                      color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                      fontSize: 15,
                      borderRadius: 2,
                      boxShadow: 3,
                      px: 2,
                      py: 1,
                      fontWeight: 400,
                      minWidth: 320,
                    },
                  },
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>Windows Credential File:</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={winCredential}
                    onChange={e => setWinCredential(e.target.value)}
                    placeholder={`C:\\Oracle\\AutoUpgrade\\Credential\\${patchKey}.xml`}
                    InputProps={{
                      sx: {
                        '&::placeholder': { color: '#8a8a8a', opacity: 1 },
                        '& .MuiInputBase-input': {
                          color: theme => theme.palette.mode === 'dark' ? '#6c757d' : '#000',
                        },
                        fontSize: 15,
                      },
                    }}
                  />
                </Box>
              </Tooltip>
            </Box>
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={deleteWinCredentialFile}
                    onChange={e => setDeleteWinCredentialFile(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Tooltip
                    title="Deletes the Microsoft Windows credential object file when the AutoUpgrade job is complete."
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                          color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                          fontSize: 15,
                          borderRadius: 2,
                          boxShadow: 3,
                          px: 2,
                          py: 1,
                          fontWeight: 400,
                          minWidth: 320,
                        },
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>Delete Windows Credential File</Typography>
                  </Tooltip>
                }
              />
            </Box>
          </>
        )}
      </Box>
      {/* Source Home, Oracle Base, Edition, and Microsoft Windows Switches (Create Home mode) */}
      {executionMode === 'create_home' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          {/* Source Home Switch and Textbox */}
          <Tooltip
            title="Specify the Source Oracle Home from which AutoUpgrade patching can derive the target version."
            placement="right"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                  color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                  fontSize: 15,
                  borderRadius: 2,
                  boxShadow: 3,
                  px: 2,
                  py: 1,
                  fontWeight: 400,
                  minWidth: 320,
                },
              },
            }}
          >
            <FormControlLabel
              sx={{ width: 'fit-content' }}
              control={
                <Switch
                  checked={effectiveSourceHomeEnabled}
                  onChange={e => effectiveSetSourceHomeEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label={<Typography sx={{ fontWeight: 600, fontSize: 16 }}>Source Home</Typography>}
            />
          </Tooltip>
          {effectiveSourceHomeEnabled && (
            <TextField
              fullWidth
              size="small"
              value={sourceHomeTouched ? sourceHome : sourceHome || sourceHomePlaceholder}
              onChange={handleSourceHomeChange}
              onFocus={handleSourceHomeFocus}
              placeholder={sourceHomePlaceholder}
              sx={{ mt: 1 }}
              InputProps={{
                sx: {
                  '&::placeholder': { color: '#8a8a8a', opacity: 1 },
                  '& .MuiInputBase-input': {
                    color:
                      !sourceHomeTouched && (sourceHome === '' || sourceHome === sourceHomePlaceholder)
                        ? '#8a8a8a'
                        : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                  },
                  fontSize: 15,
                },
              }}
            />
          )}
          {/* Oracle Base Switch and Textbox */}
          <Tooltip
            title="Specifies the directory that should be used for the Oracle base directory."
            placement="right"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                  color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                  fontSize: 15,
                  borderRadius: 2,
                  boxShadow: 3,
                  px: 2,
                  py: 1,
                  fontWeight: 400,
                  minWidth: 320,
                },
              },
            }}
          >
            <FormControlLabel
              sx={{ width: 'fit-content' }}
              control={
                <Switch
                  checked={oracleBaseEnabled}
                  onChange={e => setOracleBaseEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label={<Typography sx={{ fontWeight: 600, fontSize: 16 }}>Oracle Base</Typography>}
            />
          </Tooltip>
          {oracleBaseEnabled && (
            <TextField
              fullWidth
              size="small"
              value={oracleBaseTouched ? oracleBase : oracleBase || (isWindows ? 'C:\\Oracle' : '/u01/app/oracle')}
              onChange={e => {
                setOracleBaseTouched(true);
                setOracleBase(e.target.value);
              }}
              onFocus={() => {
                if (!oracleBaseTouched && (oracleBase === '/u01/app/oracle' || oracleBase === 'C:\\Oracle')) {
                  setOracleBase('');
                }
                setOracleBaseTouched(true);
              }}
              placeholder={isWindows ? 'C:\\Oracle' : '/u01/app/oracle'}
              sx={{ width: '100%' }}
              InputProps={{
                sx: {
                  '&::placeholder': { color: '#8a8a8a', opacity: 1 },
                  '& .MuiInputBase-input': {
                    color:
                      !oracleBaseTouched && (oracleBase === '' || oracleBase === '/u01/app/oracle' || oracleBase === 'C:\\Oracle')
                        ? '#8a8a8a'
                        : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
                  },
                  fontSize: 15,
                },
              }}
            />
          )}
          {/* Edition Switch and RadioGroup */}
          <Tooltip
            title="Specifies the Oracle Database edition that you want to use for creating the target ORACLE_HOME."
            placement="right"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                  color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                  fontSize: 15,
                  borderRadius: 2,
                  boxShadow: 3,
                  px: 2,
                  py: 1,
                  fontWeight: 400,
                  minWidth: 320,
                },
              },
            }}
          >
            <FormControlLabel
              sx={{ width: 'fit-content' }}
              control={
                <Switch
                  checked={editionEnabled}
                  onChange={e => setEditionEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label={<Typography sx={{ fontWeight: 600, fontSize: 16 }}>Edition</Typography>}
            />
          </Tooltip>
          {editionEnabled && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 42 }} />
              <RadioGroup
                row
                value={edition}
                onChange={e => setEdition(e.target.value)}
              >
                <FormControlLabel value="se2" control={<Radio size="small" />} label="Standard Edition 2" />
                <FormControlLabel value="ee" control={<Radio size="small" />} label="Enterprise Edition" />
              </RadioGroup>
            </Box>
          )}
          {/* oraInventory and Inventory Group Switchers (Linux/Unix only) */}
          {!isWindows && (
            <>
              {/* oraInventory Switch and Textbox */}
              <Tooltip
                title="Specifies the directory that should be used for the Oracle Database inventory (oraInventory) directory. Required when creating a new Oracle Home on a machine with no existing Oracle Inventory. If the inventory does not exist, this directory will be created."
                placement="right"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: (theme: any) => ({
                      bgcolor: theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                      color: theme.palette.mode === 'dark' ? '#222' : '#fff',
                      fontSize: 15,
                      borderRadius: 2,
                      boxShadow: 3,
                      px: 2,
                      py: 1,
                      fontWeight: 400,
                      minWidth: 320,
                    }),
                  },
                }}
              >
                <FormControlLabel
                  sx={{ width: 'fit-content' }}
                  control={
                    <Switch
                      checked={oraInventoryEnabled}
                      onChange={e => setOraInventoryEnabled(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={<Typography sx={{ fontWeight: 600, fontSize: 16 }}>oraInventory</Typography>}
                />
              </Tooltip>
              {oraInventoryEnabled && (
                <TextField
                  fullWidth
                  size="small"
                  value={oraInventory}
                  onChange={e => setOraInventory(e.target.value)}
                  placeholder={'/u01/app/oraInventory'}
                  sx={{ mt: 1 }}
                  InputProps={{
                    sx: {
                      '&::placeholder': { color: '#8a8a8a', opacity: 1 },
                      '& .MuiInputBase-input': {
                        color:
                          oraInventory === ''
                            ? '#8a8a8a'
                            : (theme: any) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                      },
                      fontSize: 15,
                    },
                  }}
                />
              )}
              {/* Inventory Group Switch and Textbox */}
              <Tooltip
                title="Members of the Oracle Inventory group are granted OINSTALL privileges to read and write to the Oracle Inventory directory (oraInventory). Oracle software owners are members of this group. When oraInst.loc already exists on the system, the default value matches the specified operating system group that is already defined on the system. Otherwise, the default value is oinstall."
                placement="right"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: (theme: any) => ({
                      bgcolor: theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                      color: theme.palette.mode === 'dark' ? '#222' : '#fff',
                      fontSize: 15,
                      borderRadius: 2,
                      boxShadow: 3,
                      px: 2,
                      py: 1,
                      fontWeight: 400,
                      minWidth: 320,
                    }),
                  },
                }}
              >
                <FormControlLabel
                  sx={{ width: 'fit-content' }}
                  control={
                    <Switch
                      checked={inventoryGroupEnabled}
                      onChange={e => setInventoryGroupEnabled(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={<Typography sx={{ fontWeight: 600, fontSize: 16 }}>Inventory Group</Typography>}
                />
              </Tooltip>
              {inventoryGroupEnabled && (
                <TextField
                  fullWidth
                  size="small"
                  value={inventoryGroup}
                  onChange={e => setInventoryGroup(e.target.value)}
                  placeholder={'oinstall'}
                  sx={{ mt: 1 }}
                  InputProps={{
                    sx: {
                      '&::placeholder': { color: '#8a8a8a', opacity: 1 },
                      '& .MuiInputBase-input': {
                        color:
                          inventoryGroup === ''
                            ? '#8a8a8a'
                            : (theme: any) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                      },
                      fontSize: 15,
                    },
                  }}
                />
              )}
            </>
          )}
          {/* Microsoft Windows Switch (tooltip only in Create Home mode) */}
          <Tooltip
            title="Change paths for Windows standards."
            placement="right"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                  color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                  fontSize: 15,
                  borderRadius: 2,
                  boxShadow: 3,
                  px: 2,
                  py: 1,
                  fontWeight: 400,
                  minWidth: 220,
                },
              },
            }}
          >
            <FormControlLabel
              sx={{ width: 'fit-content' }}
              control={
                <Switch
                  checked={platform === 'WINDOWS.X64'}
                  onChange={e => setPlatform(e.target.checked ? 'WINDOWS.X64' : 'LINUX.X64')}
                  color="primary"
                />
              }
              label={<Typography sx={{ fontWeight: 600, fontSize: 16 }}>Microsoft Windows</Typography>}
            />
          </Tooltip>
        </Box>
      )}
    </Paper>
  );
};

export function getPatchValidationMessages({
  downloadMode,
  executionMode,
  patchKey,
  editionEnabled,
  oracleBaseEnabled,
  patchOptions,
  targetVersionEnabled,
  sourceHomeEnabled,
  sourceHome,
}: any) {
  let messages: { type: 'success' | 'warning'; content: React.ReactNode }[] = [];
  if (downloadMode && executionMode === 'create_home') {
    const hasTargetVersion = typeof targetVersionEnabled === 'boolean' ? targetVersionEnabled : false;
    const hasSourceHome = typeof sourceHomeEnabled === 'boolean' ? sourceHomeEnabled && ((!!sourceHome && sourceHome.trim() !== '') || (!sourceHome || sourceHome.trim() === '')) : false;
    const hasEdition = editionEnabled;
    const hasOracleBase = oracleBaseEnabled;
    const ruVersionValid = typeof patchOptions.ruVersion === 'string' && /^\d+\.\d+$/.test(patchOptions.ruVersion.trim());
    const hasRU = patchOptions.ru && ruVersionValid;
    const showMainValidation = !hasSourceHome && !hasTargetVersion && !hasRU;
    const showBothEditionAndOracleBase = !hasSourceHome && (!hasEdition || !hasOracleBase);
    const showValidConfig = hasSourceHome || ((hasEdition && hasOracleBase) && (hasRU || hasTargetVersion));
    let errorLines: React.ReactNode[] = [];
    if (showMainValidation) {
      errorLines.push(
        <React.Fragment key="main">
          <Typography sx={{ fontWeight: 600, color: '#ff5252', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <span style={{ fontSize: 22, verticalAlign: 'middle' }}>⚠️</span>
            AutoUpgrade Patching could not derive the target version for prefix <b>{patchKey}</b>.
          </Typography>
          <Typography sx={{ fontSize: 15, color: '#ffbdbd', mb: 1 }}>Do one of the following:</Typography>
          <ol style={{ margin: 0, paddingLeft: 22, color: '#ffbdbd', fontSize: 15 }}>
            <li>Specify the <b>source_home</b> parameter</li>
            <li>Specify the <b>patch</b> parameter using <b>RU:x.y</b> notation</li>
            <li>Specify the <b>target_version</b> parameter</li>
          </ol>
        </React.Fragment>
      );
      errorLines.push(
        <Typography key="invalid" sx={{ fontWeight: 700, color: '#ff5252', mt: 2, pl: 2 }}>
          The current configuration is INVALID.
        </Typography>
      );
    } else if (showBothEditionAndOracleBase) {
      if (!hasEdition) {
        errorLines.push(
          <Typography key="edition" sx={{ fontSize: 15, color: '#ffbdbd', mt: 2 }}>
            <span style={{ fontWeight: 600, color: '#ff5252', marginRight: 6, fontSize: 18, verticalAlign: 'middle' }}>⚠️</span>
            AutoUpgrade Patching is unable to derive the edition that is needed to create the target ORACLE_HOME. Specify either the <b>source_home</b> or the <b>home_settings.edition</b> configuration parameter for prefix <b>{patchKey}</b>.
          </Typography>
        );
      }
      if (!hasOracleBase) {
        errorLines.push(
          <Typography key="oraclebase" sx={{ fontSize: 15, color: '#ffbdbd', mt: 2 }}>
            <span style={{ fontWeight: 600, color: '#ff5252', marginRight: 6, fontSize: 18, verticalAlign: 'middle' }}>⚠️</span>
            AutoUpgrade Patching is unable to derive the ORACLE_BASE value that is needed to create the target ORACLE_HOME. Specify either the <b>source_home</b> or the <b>home_settings.oracle_base</b> configuration parameter for prefix <b>{patchKey}</b>.
          </Typography>
        );
      }
      errorLines.push(
        <Typography key="invalid" sx={{ fontWeight: 700, color: '#ff5252', mt: 2, pl: 2 }}>
          The current configuration is INVALID.
        </Typography>
      );
    }
    if (errorLines.length > 0) {
      messages = errorLines.map(content => ({ type: 'warning', content }));
    } else if (showValidConfig) {
      messages = [{
        type: 'success',
        content: (
          <Box sx={{ mt: 2, p: 2.5, border: '2px solid #388e3c', borderRadius: 3, background: '#388e3c', color: '#fff', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <CheckCircleIcon sx={{ fontSize: 28, color: '#b9f6ca', mr: 1 }} />
            <Typography sx={{ fontWeight: 600, color: '#fff', fontSize: 19 }}>The current configuration is VALID.</Typography>
          </Box>
        )
      }];
    }
  } else if (downloadMode) {
    const hasSourceHome = typeof sourceHomeEnabled === 'boolean' ? sourceHomeEnabled && ((!!sourceHome && sourceHome.trim() !== '') || (!sourceHome || sourceHome.trim() === '')) : false;
    const hasTargetVersion = typeof targetVersionEnabled === 'boolean' ? targetVersionEnabled : false;
    const ruVersionValid = typeof patchOptions.ruVersion === 'string' && /^\d+\.\d+$/.test(patchOptions.ruVersion.trim());
    const hasRU = patchOptions.ru && ruVersionValid;
    const showMainValidation = !hasSourceHome && !hasTargetVersion && !hasRU;
    const showValidConfig = hasSourceHome || hasTargetVersion || hasRU;
    let errorLines: React.ReactNode[] = [];
    if (showMainValidation) {
      errorLines.push(
        <React.Fragment key="main">
          <Typography sx={{ fontWeight: 600, color: '#ff5252', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <span style={{ fontSize: 22, verticalAlign: 'middle' }}>⚠️</span>
            AutoUpgrade Patching could not derive the target version for prefix <b>{patchKey}</b>.
          </Typography>
          <Typography sx={{ fontSize: 15, color: '#ffbdbd', mb: 1 }}>Do one of the following:</Typography>
          <ol style={{ margin: 0, paddingLeft: 22, color: '#ffbdbd', fontSize: 15 }}>
            <li>Specify the <b>source_home</b> parameter</li>
            <li>Specify the <b>patch</b> parameter using <b>RU:x.y</b> notation</li>
            <li>Specify the <b>target_version</b> parameter</li>
          </ol>
        </React.Fragment>
      );
      errorLines.push(
        <Typography key="invalid" sx={{ fontWeight: 700, color: '#ff5252', mt: 2, pl: 2 }}>
          The current configuration is INVALID.
        </Typography>
      );
    }
    if (errorLines.length > 0) {
      messages = errorLines.map(content => ({ type: 'warning', content }));
    } else if (showValidConfig) {
      messages = [{
        type: 'success',
        content: (
          <Box sx={{ mt: 2, p: 2.5, border: '2px solid #388e3c', borderRadius: 3, background: '#388e3c', color: '#fff', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <CheckCircleIcon sx={{ fontSize: 28, color: '#b9f6ca', mr: 1 }} />
            <Typography sx={{ fontWeight: 600, color: '#fff', fontSize: 19 }}>The current configuration is VALID.</Typography>
          </Box>
        )
      }];
    }
  }
  return messages;
}

export default BasicInformation; 